// Copyright 2021 Rennie Glen Software LLC

import { ProgressMessage } from "./Progress";

export type Response = Ok | Error | WorkerReady | Progress | WorkerFatal;
export interface Ok {
    kind: "Ok";
    requestId: number;
    returnValue: any;
}

export interface Error {
    kind: "Error";
    requestId: number;
    error: any;    
}

export interface WorkerReady {
    kind: "WorkerReady";
}

export interface WorkerFatal {
    kind: "WorkerFatal";
    error: unknown;
}

export function ok(requestId: number, returnValue: any): Ok {
    return {
        kind: "Ok",
        requestId: requestId,
        returnValue: returnValue
    };
}

export function error(requestId: number, error: any): Error {
    return {
        kind: "Error",
        requestId: requestId,
        error: error
    };
}

export type Request = Operate | Xray | AddNestingImage
    | ClearNestingImages | ShardStickers | NestStickers
    | ImposeStickers | ReduceStickers | GetPageBoxes;

// A file that resulted from an operation.
export interface FileAndDimensions {
    file: File;
    displayName: string;
    dimensions: number[];
    jdfFile?: File;
    cutBlocks: number[][][];  // In canvas space
    layers: string[];
    id: string;  // May be empty
}

export function emptyFileAndDimensions(): FileAndDimensions {
    return {
        file: new File([], ""),
        dimensions: [],
        cutBlocks: [],
        layers: [],
        displayName: "",
        id: "",
    };
}

export function fileAndNoDimensions(file: File): FileAndDimensions {
    return {
        file,
        dimensions: [],
        cutBlocks: [],
        layers: [],
        displayName: file.name,
        id: "",
    };
}


export function fileAndDimensonsFrom(r: OneOperateResult): FileAndDimensions {
    return {
        dimensions: r.dimensions,
        cutBlocks: r.cutBlocks,
        file: new File([r.fileBytes], r.fileName, { type: 'application/pdf'}),
        jdfFile: new File([r.jdfBytes], r.fileName + ".jdf"),
        layers: r.layers,
        displayName: r.displayName,
        id: r.id        
    }
}

export interface Rectangle {
    left: number,
    right: number,
    bottom: number,
    top: number
}

export interface PageBoxes {
    /// The minimum of the media box and the crop box.
    pageRect: Rectangle,
    // Other boxes pulled directly from the page object.
    mediaBox: Rectangle,
    cropBox?: Rectangle,
    trimBox?: Rectangle,
    artBox?: Rectangle,
    bleedBox?: Rectangle,
}

export function dimensionsFrom(boxes: PageBoxes[]): number[] {
    return boxes.flatMap(boxes => [
        Math.abs(boxes.pageRect.right - boxes.pageRect.left),
        Math.abs(boxes.pageRect.top - boxes.pageRect.bottom)
    ])
}

export interface GetPageBoxes {
    kind: "GetPageBoxes",
    requestId: number;
    file: File;
    range?: [number, number];
}

export interface GetPageBoxesResult {
    boxes: PageBoxes[],
    layers: string[],
    id: string,
}

/// Returns Uint8Array of HTML
export interface Xray {
    kind: "Xray";
    requestId: number;
    file: File;
}

// Returns: OperateResult.
export interface Operate {
    kind: "Operate";
    requestId: number;
    files: File[];
    operations: Record<string, number | string | boolean>[];
    overlays: (Uint8Array | null)[];
    options: OperateOptions;
}

/// Return an ArrayBuffer instead of File because ArrayBuffers are
/// transferrable from the worker thread to the UI thread and therefore don't
/// need to be copied..
export interface OneOperateResult {
    /// The name of the file when downloaded to the user's machine.
    fileName: string;
    /// The name to display in the top right corner of the UI.
    displayName: string;
    fileBytes: ArrayBuffer;
    dimensions: number[];
    jdfBytes: ArrayBuffer;
    cutBlocks: number[][][];
    layers: string[];
    id: string;
}

export interface OperateResult {
    files: OneOperateResult[];
}

export interface AddNestingImage {
    kind: "AddNestingImage",
    requestId: number;
    pageNumber: number;
    rotation: number;
    rotate90: boolean;
    dataUrl: string;
    whiteIsTransparent: boolean;
}

export interface ClearNestingImages {
    kind: "ClearNestingImages",
    requestId: number;
}

/// After repeated calls to AdNestingImage, builds a nesting palette.
/// Splits a stickers task into parallel shards.
export interface ShardStickers {
    kind: "ShardStickers",
    requestId: number,
    /// Values collected from NestParams.
    nestValues: Record<string, any>
}

export interface ShardStickersResult {
    palette: unknown,
    sourcePageCounts: unknown,
    shards: unknown[]
}

/// Calculates the optimal nesting plan.
/// Returns plans, an opaque type to be passed to ImposeStickers.
export interface NestStickers {
    kind: "NestStickers",
    requestId: number,
    palette: unknown,
    sourcePageCounts: unknown,
    /// One value returned from ShardStickers.
    shard: unknown
}

/// Calculates the optimal nesting plan.
/// Returns plans, an opaque type to be passed to ImposeStickers.
export interface ReduceStickers {
    kind: "ReduceStickers",
    requestId: number,
    /// All the results from NestStickers.
    nests: unknown[]
}

export type ReduceStickersResult = {
    done: boolean,
    nest?: unknown,
    sourcePageCounts?: unknown,
}


/// Creates a new PDF document according to the palette and the plans.
/// Returns OperateResult.
export interface ImposeStickers {
    kind: "ImposeStickers",
    requestId: number,
    files: File[],
    /// Values collected from NestParams.
    nestValues: Record<string, any>
    palette: any,
    /// All the plans returned from NestStickers.
    nests: any[]
}

export enum Exceptions {
    Encrypted = "EncryptedException"
}

export interface EncryptedException {
    kind: Exceptions.Encrypted;
    fileName: string;
}

export interface Progress {
    kind: "Progress";
    requestId: number;
    progress: ProgressMessage[];
}

export type AppendName = {
  kind: "Nope"  
} | {
  kind: "WithOpNames"
} | { 
  kind: "WithText",
  text: string
};

export type OperateOptions = {
  appendFileName: AppendName
}

export function defaultOperateOptions(): OperateOptions {
    return { appendFileName: { kind: "Nope"}}
}